import React, { useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import PageMeta from '../../components/common/PageMeta';
import { useAuth } from '../../context/AuthContext';
import {
  Callout,
  Field,
  Input,
  PasswordInput,
  PublicLink,
  PublicPage,
  PublicPrimary,
  PublicShell,
  PublicTitle,
} from '../../ui/v2';
import { classifySignInError, emailError } from './authModel';

/**
 * Sign in — `/signin`.
 *
 * One column, two fields, one primary. The forgot-password link sits on the
 * password label so it's found when it's needed. Empty or malformed fields
 * get an error under the field and focus; a failed sign-in is one sentence
 * that never says which half was wrong — the email stays, focus returns to
 * the password.
 *
 * Router state: `message` (a notice, e.g. from the reset flow) and `from`
 * (where to land after signing in; defaults to `/`).
 *
 * Not carried over from the old page: "Keep me logged in" (`login()` never
 * read it), the "Back to dashboard" link and the marketing half-page.
 */
export default function SignIn() {
  const { login } = useAuth();
  const location = useLocation() as {
    state?: { message?: string; from?: string } | null;
  };
  const notice = location.state?.message ?? null;
  const from = location.state?.from ?? null;

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [errors, setErrors] = useState<{ email?: string; password?: string }>(
    {},
  );
  const [serverError, setServerError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const emailRef = useRef<HTMLInputElement>(null);
  const passwordRef = useRef<HTMLInputElement>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (busy) return;
    const next = {
      email: emailError(email),
      password: password ? undefined : 'Enter your password.',
    };
    setErrors(next);
    setServerError(null);
    if (next.email) return emailRef.current?.focus();
    if (next.password) return passwordRef.current?.focus();

    setBusy(true);
    try {
      const result = await login(email.trim(), password, false);
      if (result.success) {
        window.location.href = from || '/';
        return;
      }
      setServerError(classifySignInError(result.error));
    } catch (err) {
      setServerError(
        classifySignInError(err instanceof Error ? err.message : undefined),
      );
    }
    setBusy(false);
    passwordRef.current?.focus();
  };

  return (
    <PublicPage>
      <PageMeta
        title="Sign in · AskArthur"
        description="Sign in to AskArthur"
      />
      <PublicShell width="form">
        <PublicTitle>Sign in</PublicTitle>
        {notice && (
          <Callout tone="accent" className="mt-5">
            {notice}
          </Callout>
        )}
        <form onSubmit={submit} noValidate className="mt-6 flex flex-col gap-4">
          <Field label="Email" error={errors.email}>
            {(id, describedBy) => (
              <Input
                ref={emailRef}
                id={id}
                type="email"
                inputMode="email"
                autoComplete="username"
                autoFocus
                aria-describedby={describedBy}
                invalid={!!errors.email}
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  if (errors.email)
                    setErrors((x) => ({ ...x, email: undefined }));
                }}
                className="h-10 text-v2-14"
              />
            )}
          </Field>
          <Field
            label="Password"
            error={errors.password}
            action={
              <PublicLink
                to="/forgot-password"
                className="text-v2-12 font-normal"
              >
                Forgot password?
              </PublicLink>
            }
          >
            {(id, describedBy) => (
              <PasswordInput
                ref={passwordRef}
                id={id}
                autoComplete="current-password"
                aria-describedby={describedBy}
                invalid={!!errors.password}
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  if (errors.password)
                    setErrors((x) => ({ ...x, password: undefined }));
                }}
                className="[&>input]:h-10 [&>input]:text-v2-14"
              />
            )}
          </Field>
          {serverError && <Callout tone="danger">{serverError}</Callout>}
          <PublicPrimary
            type="submit"
            loading={busy}
            className="mt-1 sm:w-full"
          >
            Sign in
          </PublicPrimary>
        </form>
        <p className="mt-6 text-v2-12 text-v2-fg-3">
          New to AskArthur?{' '}
          <a
            href="https://www.askarthur.ai/contact"
            target="_blank"
            rel="noopener noreferrer"
            className="text-v2-fg-2 underline-offset-2 hover:underline"
          >
            Talk to us
          </a>{' '}
          — accounts are set up with you, not by a form.
        </p>
      </PublicShell>
    </PublicPage>
  );
}
